<template>
  <div class="game-stats">
    <div v-if="!generatedAt" class="flex flex-column items-center justify-center no-results">
      <UIIcon name="no-results" class="no-results-icon mb5"/>
      <p
        class="f5 tc"
      >{{ $t("This title wasn’t available on SpringboardVR at the time you selected.") }}</p>
    </div>
    <template v-else>
      <ContentBlock>
        <div slot="instructions">
          <h4 class="gametitle f3">{{ $t('Licensed through SpringboardVR') }}</h4>
          <p
            class="f6"
          >{{ $t('These are the statistics for licenses purchased from the SpringboardVR Marketplace.') }}</p>
        </div>
        <div class="block">
          <div>
            <div class="mb4" v-if="!this.$permissions['enterprise-developer']">
              <h6 class="f7 currentrevenuelabel flex items-center">
                <img src="/img/money.svg" :alt="$t('Money')" class="money-icon mr1" />
                {{ type === 'month' ? $t('Month Revenue') : $t('Year Revenue')}}*
              </h6>
              <div class="flex items-center">
                <span class="f1 currentrevenue">{{ stat.currentRevenue || $t('Unknown') }}</span>
                <span class="f4 currentrevenue ml3" v-if="stat.currentRevenue">{{ $t('USD') }}</span>
              </div>
            </div>
            <div class="grid">
              <div>
                <h6 class="f7">{{ $t('Total Minutes') }}</h6>
                <span class="f3 totalminutes">{{ stat.totalMinutes || $t('Unknown') }}</span>
              </div>
              <div v-if="!this.$permissions['enterprise-developer']">
                <h6 class="f7">{{ $t('Number of Organizations') }}</h6>
                <span class="f3 locationcount">{{ stat.numberOfLocations || $t('Unknown') }}</span>
              </div>
              <div>
                <h6 class="f7">{{ $t('Number of Stations') }}</h6>
                <span class="f3 stationcount">{{ stat.numberOfStations || $t('Unknown') }}</span>
              </div>
              <div>
                <h6 class="f7">{{ $t('Average Session Time (min)') }}</h6>
                <span class="f3 averagesessiontime">{{ stat.averageSessionTime || $t('Unknown') }}</span>
              </div>
            </div>
          </div>
        </div>
        <div slot="footer">
          <div class="footer pv4 ph4 ph5-m tl w-100">
            <a :href=stat.downloadLicensedStatsExportUrl>
              <button class="download-stats">
                <h6 class="f7 ttu mb2">
                  <UIIcon name="download" class="download-icon fill-red"/>
                  {{ $t('Download stats') }}
                </h6>
              </button>
            </a>
            <p
              class="f7"
            >{{ $t('Download a .csv file of stats about organizations licensing your title, license details and revenue for the selected timeframe.') }}</p>
          </div>
        </div>
      </ContentBlock>
      <ContentBlock>
        <div slot="instructions">
          <h4 class="gametitle f3">{{ $t('Not Licensed through SpringboardVR') }}</h4>
          <p
            class="f6"
          >{{ $t('These are the statistics for when your title is side-loaded onto SpringboardVR. This may include direct deals or licenses from another source such as Steam Site Licensing.') }}</p>
        </div>
        <div class="block">
          <div class="grid">
            <div>
              <h6 class="f7">{{ $t('Total Minutes') }}</h6>
              <span class="f3 totalminutes">{{ stat.unlicensedTotalMinutes || $t('Unknown') }}</span>
            </div>
            <div v-if="!this.$permissions['enterprise-developer']">
              <h6 class="f7">{{ $t('Number of Organizations') }}</h6>
              <span class="f3 locationcount">{{ stat.unlicensedNumberOfLocations || $t('Unknown') }}</span>
            </div>
            <div>
              <h6 class="f7">{{ $t('Number of Stations') }}</h6>
              <span class="f3 stationcount">{{ stat.unlicensedNumberOfStations || $t('Unknown') }}</span>
            </div>
          </div>
        </div>
        <div slot="footer">
          <div class="footer pv4 ph4 ph5-m tl w-100">
            <a :href=stat.downloadUnlicensedStatsExportUrl>
              <button class="download-stats">
                <h6 class="f7 ttu mb2">
                  <svg class="download-icon fill-red">
                    <use xlink:href="#icon-download" />
                  </svg>
                  {{ $t('Download stats') }}
                </h6>
              </button>
            </a>
            <p
              class="f7"
            >{{ $t('Download a .csv file of stats regarding organizations side-loading your title for the selected timeframe.') }}</p>
          </div>
        </div>
      </ContentBlock>
      <ContentBlock>
        <div slot="instructions">
          <h4 class="gametitle f3">{{ $t('Total Stats') }}</h4>
          <p
            class="f6"
          >{{ $t('These are the combined statistics for licenses purchased through SpringboardVR and side-loaded content.') }}</p>
        </div>
        <div class="block">
          <div class="grid">
            <div>
              <h6 class="f7">{{ $t('Total Minutes') }}</h6>
              <span class="f3 totalminutes">{{ stat.totalTotalMinutes || $t('Unknown') }}</span>
            </div>
            <div v-if="!this.$permissions['enterprise-developer']">
              <h6 class="f7">{{ $t('Number of Organizations') }}</h6>
              <span class="f3 locationcount">{{ stat.totalNumberOfLocations || $t('Unknown') }}</span>
            </div>
            <div>
              <h6 class="f7">{{ $t('Number of Stations') }}</h6>
              <span class="f3 stationcount">{{ stat.totalNumberOfStations || $t('Unknown') }}</span>
            </div>
          </div>
        </div>
        <div slot="additional" class="updatedat" v-if="generatedAt">
          <p class="f7" v-if="isCurrentDate">{{ $t('Latest Update') }}: {{ generatedAt || '-' }}</p>
          <p
            class="mt1 f7"
          >{{ $t('*This is an estimation and does not include any service fees or uncollected payments.') }}</p>
          <p class="updatedat">
            <slot name="additional"></slot>
          </p>
        </div>
      </ContentBlock>
    </template>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import GameStats from './GameStats';
import UIIcon from '@/core/shared/components/ui/UIIcon';

@Component({
  components: {
    UIIcon,
  },
})
export default class NewGameStats extends GameStats {
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";
.game-stats {
  & .no-results {
    color: var(--colorSlate);

    & .no-results-icon {
      fill: var(--colorGeyser);
      width: 10rem;
      height: 10rem;
    }
  }
  & h6,
  & .gametitle {
    margin: 0;
    margin-bottom: var(--spacingXS);

    font-style: normal;
  }
  & .grid {
    display: grid;
    grid-gap: var(--spacingMd);
    grid-template-columns: repeat(2, 1fr);
    @media (--tablet) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  & .updatedat {
    margin-top: var(--spacingSm);
  }

  & .footer {
    background-color: var(--colorCasper);

    & .download-stats {
      border: none;
      background-color: transparent;
      & h6 {
        color: var(--colorSBRedFlat);
        font-weight: bold;
      }
    }
    & .download-stats:hover {
      cursor: pointer;
      & h6 {
        color: #c7402b;
      }
      & .download-icon {
        fill: #c7402b;
      }
    }
  }
}

.currentrevenuelabel,
.currentrevenue {
  color: var(--colorInfoFlat);
}

.instructions p {
  color: var(--colorSlate);
}

.money-icon {
  width: 2.5rem;
}

.download-icon {
  width: 1.5rem;
  height: 1.5rem;
}
</style>
